import React, { useState, useEffect } from 'react';
import api from '../../../../api';

const CreateCohorts = ({ uid }) => {
  const [cohortName, setCohortName] = useState('');
  const [branch, setBranch] = useState('');
  const [semester, setSemester] = useState('');
  const [classname, setClassname] = useState('');
  const [academicYear, setAcademicYear] = useState('');
  const [cohorts, setCohorts] = useState([]);
  const [branches, setBranches] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [countdown, setCountdown] = useState(0);

  const fetchBranches = async () => {
    try {
      const response = await api.get('api/branch/show');
      setBranches(response.data);
    } catch (error) {
      console.error('Error fetching branches:', error);
      setBranches([]);
      setErrorMessage('Error fetching branches. Please try again.');
    }
  };

  const getAcademicYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = 0; i < 5; i++) {
      const startYear = currentYear - i;
      const endYear = startYear + 1;
      years.push(`${startYear}-${endYear.toString().slice(-2)}`);
    }
    return years;
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const validateSemester = (value) => {
    const num = parseInt(value, 10); // Convert to number
    if (!isNaN(num) && num >= 1 && num <= 8) {
      setSemester(value); // Set only if valid
    } else {
      setSemester(''); // Clear input if invalid
      setErrorMessage('Semester must be a number between 1 and 8.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    setCountdown(10);

    try {
      await api.post('/api/cohorts/', {
        user_id: uid,
        cohort_name: cohortName,
        branch,
        semester: Number(semester), // Convert semester to number before submission
        classname,
        academic_year: academicYear,
      });
      setSuccessMessage('Cohort created successfully!');
      resetForm();
      startTimer();
    } catch (error) {
      console.error('Error creating cohort:', error);
      setErrorMessage('Error creating cohort. Please try again.');
      startTimer();
    }
  };

  const startTimer = () => {
    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev === 1) {
          clearInterval(interval);
          setSuccessMessage('');
          setErrorMessage('');
        }
        return prev - 1;
      });
    }, 1000);
  };

  const resetForm = () => {
    setCohortName('');
    setBranch('');
    setSemester('');
    setClassname('');
    setAcademicYear('');
  };

  const academicYears = getAcademicYears();

  return (
    <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <div className="bg-white shadow-lg rounded-lg p-8 w-full max-w-2xl">
        <h2 className="text-center text-3xl font-extrabold text-indigo-600 mb-6">
          Create Cohorts
        </h2>

        {successMessage && (
          <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-6 rounded-lg">
            {successMessage}
          </div>
        )}

        {errorMessage && (
          <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-lg">
            {errorMessage}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="cohortName">
              Cohort Name
            </label>
            <input
              type="text"
              id="cohortName"
              value={cohortName}
              onChange={(e) => setCohortName(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="branch">
              Branch
            </label>
            <select
              id="branch"
              value={branch}
              onChange={(e) => setBranch(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            >
              <option value="" disabled>Select branch</option>
              {branches.length > 0
                ? branches.map((b) => (
                    <option key={b.idbranch} value={b.idbranch}>
                      {b.branchname}
                    </option>
                  ))
                : <option disabled>No branches available</option>}
            </select>
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="semester">
              Semester
            </label>
            <input
              type="text"
              id="semester"
              value={semester}
              onChange={(e) => validateSemester(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              placeholder="Enter a number between 1 and 8"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="classname">
              Class Name
            </label>
            <input
              type="text"
              id="classname"
              value={classname}
              onChange={(e) => setClassname(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="academicYear">
              Academic Year
            </label>
            <select
              id="academicYear"
              value={academicYear}
              onChange={(e) => setAcademicYear(e.target.value)}
              className="w-full p-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400"
              required
            >
              <option value="" disabled>Select academic year</option>
              {academicYears.map((year, index) => (
                <option key={index} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>

          <button
            type="submit"
            className="w-full bg-indigo-600 text-white p-3 rounded-lg font-semibold hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-400"
          >
            Create Cohort
          </button>
        </form>
      </div>
    </div>
  );
};

export default CreateCohorts;
